import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Tags, FileText, Megaphone, Settings, LogOut, Menu, Bell, Sparkles, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/inbox',     label: 'Inbox',     icon: MessageSquare,   testid: 'nav-inbox' },
  { to: '/contacts',  label: 'Contacts',  icon: Users,           testid: 'nav-contacts' },
  { to: '/groups',    label: 'Groups',    icon: Tags,            testid: 'nav-groups' },
  { to: '/templates', label: 'Templates', icon: FileText,        testid: 'nav-templates' },
  { to: '/campaigns', label: 'Campaigns', icon: Megaphone,       testid: 'nav-campaigns' },
  { to: '/settings',  label: 'Settings',  icon: Settings,        testid: 'nav-settings' },
];

const Brand = ({ size = 'md' }) => (
  <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-link">
    <div className="h-9 w-9 rounded-2xl gradient-orange grid place-items-center shadow-md">
      <Sparkles className="h-5 w-5 text-white" />
    </div>
    <div className="leading-tight">
      <div className={`font-bold text-[hsl(var(--hj-ink))] ${size === 'lg' ? 'text-lg' : 'text-base'}`}>HJ4U Campaigns</div>
      <div className="text-[10.5px] text-muted-foreground tracking-wide uppercase">Happy Journey 4U</div>
    </div>
  </Link>
);

function NavItem({ item, onClick, badge }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={item.testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-colors duration-200 ` +
        (isActive
          ? 'bg-[#FFE2D1]/80 text-[#9A3412] border border-white/60 font-semibold shadow-sm'
          : 'text-[hsl(var(--foreground))]/80 hover:bg-white/55 hover:text-[hsl(var(--foreground))]')
      }
    >
      <Icon className="h-4.5 w-4.5" />
      <span className="flex-1">{item.label}</span>
      {badge > 0 && (
        <span className="text-[10px] font-semibold bg-[#F25C2A] text-white rounded-full px-1.5 min-w-[18px] h-[18px] grid place-items-center">{badge > 99 ? '99+' : badge}</span>
      )}
    </NavLink>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetch = () => api.get('/inbox/conversations/unread-count').then((r) => !cancelled && setInboxUnread(r.data?.count || 0)).catch(() => {});
    fetch();
    const t = setInterval(fetch, 7000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentTitle = NAV.find((n) => location.pathname.startsWith(n.to))?.label || 'Dashboard';

  return (
    <div className="min-h-screen flex w-full">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex lg:w-[260px] xl:w-[280px] flex-col gap-4 p-4 sticky top-0 h-screen"
        data-testid="app-sidebar"
      >
        <Brand size="lg" />
        <nav className="glass-card p-2 flex flex-col gap-1 flex-1">
          {NAV.map((n) => <NavItem key={n.to} item={n} badge={n.to === '/inbox' ? inboxUnread : 0} />)}
          {user?.role === 'admin' && (
            <NavItem item={{ to: '/users', label: 'Team', icon: Users, testid: 'nav-team' }} />
          )}
        </nav>
        <div className="glass-card p-3 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full gradient-orange grid place-items-center text-white font-semibold">
            {(user?.name || user?.email || 'A').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name || 'Admin'}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="sidebar-logout-button" aria-label="Logout">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-30 backdrop-blur-md bg-white/45 border-b border-white/40">
          <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-8 py-3">
            <div className="lg:hidden">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-nav-open-button" aria-label="Open menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-4 w-[280px] glass-strong border-white/40">
                  <div className="mb-4"><Brand size="lg" /></div>
                  <nav className="flex flex-col gap-1">
                    {NAV.map((n) => <NavItem key={n.to} item={n} onClick={() => setOpen(false)} />)}
                    {user?.role === 'admin' && (
                      <NavItem item={{ to: '/users', label: 'Team', icon: Users, testid: 'nav-team-mobile' }} onClick={() => setOpen(false)} />
                    )}
                  </nav>
                  <div className="mt-6 border-t border-white/50 pt-4">
                    <div className="text-xs text-muted-foreground mb-1">Signed in</div>
                    <div className="text-sm font-medium">{user?.email}</div>
                    <Button variant="outline" className="w-full mt-3" onClick={handleLogout} data-testid="mobile-logout-button">
                      <LogOut className="h-4 w-4 mr-2" /> Log out
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
            <div className="lg:hidden flex-1"><Brand /></div>
            <h1 className="hidden lg:block text-xl font-semibold tracking-tight" data-testid="page-title">{currentTitle}</h1>
            <div className="flex-1" />
            <Button variant="ghost" size="icon" aria-label="Notifications" data-testid="topbar-bell-button">
              <Bell className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-5 sm:py-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
